import { hasModifierKey } from '@angular/cdk/keycodes';
import {
  Directive,
  EventEmitter,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { merge, Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

/**
 * Routes implicit dialog dismissals (backdrop click, Escape) through the same
 * seam as an explicit Cancel button: instead of letting Material close the
 * dialog silently, the directive disables auto-close and re-emits both
 * gestures as `dismissRequested`. The host template forwards the emission to
 * the editor's `onCancel()`, which owns the discard policy (confirm dialog,
 * dirty gating, silent close, ...).
 *
 * Outside a dialog (page mode, stories) there is no `MatDialogRef` to guard,
 * so the directive is a no-op.
 *
 * Usage:
 *   <ts-some-editor #editor tsDialogDismissGuard (dismissRequested)="editor.onCancel()">
 */
@Directive({
  selector: '[tsDialogDismissGuard]',
  standalone: true,
})
export class DialogDismissGuardDirective implements OnInit, OnDestroy {
  @Output() dismissRequested = new EventEmitter<void>();

  private subscription: Subscription | null = null;

  constructor(@Optional() private readonly dialogRef: MatDialogRef<unknown> | null) {}

  ngOnInit(): void {
    if (!this.dialogRef) return;
    this.dialogRef.disableClose = true;
    this.subscription = merge(
      this.dialogRef.backdropClick(),
      this.dialogRef.keydownEvents().pipe(
        filter((event) => event.key === 'Escape' && !hasModifierKey(event)),
        // Material itself prevents default on the Escape it handles; mirror that.
        tap((event) => event.preventDefault())
      )
    ).subscribe(() => this.dismissRequested.emit());
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
